import { checkWorker } from "../../ActionUtils";
import { ActivityOptions } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { Action } from "~/modules/actions/Action";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { getLogger, LoggerName } from "~/modules/logger";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";



export interface SetWorkerActivityOptions {
    activityUpdateOptions?: ActivityOptions;
}










































export class SetWorkerActivity implements Action<Promise<void>> {
    


    readonly #targetWorkerSid: string;

    


    readonly #activitySid: string;

    


    readonly #options?: SetWorkerActivityOptions;

    constructor(targetWorkerSid: string, activitySid: string, options?: SetWorkerActivityOptions) {
        this.#targetWorkerSid = targetWorkerSid;
        this.#activitySid = activitySid;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Client);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const activityUpdateOptions = this.#options?.activityUpdateOptions;
        const worker = taskRouter.worker;

        logger.debug(
            `setWorkerActivity invoked with activitySid: ${this.#activitySid} and targetWorkerSid: ${
                this.#targetWorkerSid
            }`
        );

        checkWorker(worker, {
            actionName: "SetWorkerActivity",
            moduleName: "actions/Supervisor",
            logger,
            analytics,
            context,
            event: EVENTS.SetWorkerActivityCompleted,
            extra: {
                activitySid: this.#activitySid
            },
            worker
        });

        try {
            await worker.setWorkerActivity(this.#targetWorkerSid, this.#activitySid, activityUpdateOptions);

            sendWorkerTrackingEvent(EVENTS.SetWorkerActivityCompleted, analytics, worker, {
                targetWorkerSid: this.#targetWorkerSid,
                activitySid: this.#activitySid
            });

            return Promise.resolve();
        } catch (error: unknown) {
            const response = toCauseMessage(error);

            let errorMsg: string;
            let errorCode: ErrorCode;

            if (
                !!response &&
                /Worker WK.* cannot have its activity updated while it has .* pending reservations/.exec(response)
            ) {
                errorCode = ErrorCode.ActivityChangeRejectedPendingReservations;
                errorMsg = `SetWorkerActivity: Cannot set activity to ${
                    this.#activitySid
                } by the supervisor while current worker ${this.#targetWorkerSid} has other pending reservations`;
            } else {
                errorCode = ErrorCode.FailedToSetWorkerActivityBySupervisor;
                errorMsg = `SetWorkerActivity: Failed to set activity ${this.#activitySid} for worker ${
                    this.#targetWorkerSid
                } by the supervisor`;
            }

            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.SetWorkerActivityCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                targetWorkerSid: this.#targetWorkerSid,
                activitySid: this.#activitySid
            });
            return throwFlexSdkError(context)(errorCode, { severity: ErrorSeverity.Error }, errorMsg, error);
        }
    }
}
